import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Pagination,
  Skeleton,
  Snackbar,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";

import Panel from "../../components/brand/Panel";
import KpiTile from "../../components/brand/KpiTile";
import { getPropertyDetails } from "../../api/propertyApi";
import {
  deleteBudget,
  generateCurrentInvoices,
  generateOwnerInvoicePdf,
  getBudget,
  getBudgets,
  getCurrentInvoicePreview,
  getPropertyFinanceSummary,
  postAllCurrentDraftInvoices,
  postOwnerInvoice,
  updateBudget,
  updateBudgetStatus,
} from "../../api/financeApi";
import { getApiErrorMessage, isConcurrencyConflict } from "../../utils/concurrency";
import BudgetEditor from "./BudgetEditor";
import BudgetFormatEditor from "./BudgetFormatEditor";
import FiscalYearsPanel from "./FiscalYearsPanel";
import ApportionmentsPanel from "./ApportionmentsPanel";
import ServiceChargeMechanismPanel from "./ServiceChargeMechanismPanel";
import ServiceChargeRecalculationPanel from "./ServiceChargeRecalculationPanel";
import OwnerInvoicePreviewPanel from "./OwnerInvoicePreviewPanel";
import BankAccountsPanel from "./BankAccountsPanel";
import BankReconciliationPanel from "./BankReconciliationPanel";
import YearEndPackPanel from "./YearEndPackPanel";
import PurchaseOrdersPanel from "./PurchaseOrdersPanel";
import SupplierBillsPanel from "./SupplierBillsPanel";
import CreateBudgetWizard from "./CreateBudgetWizard";
import { fiscalYearLabel } from "./fiscalYearDates";

const BASE_FINANCE_TABS = [
  { key: "overview", label: "Overview" },
  { key: "fiscal-years", label: "Fiscal Years" },
  { key: "budgets", label: "Budgets" },
  { key: "apportionments", label: "Apportionments" },
  { key: "invoices", label: "Owner invoices" },
  { key: "bank", label: "Bank" },
  { key: "reconciliation", label: "Reconciliation" },
  { key: "year-end", label: "Year-end" },
];

const SPEND_TABS = [
  { key: "purchase-orders", label: "Purchase orders" },
  { key: "supplier-bills", label: "Supplier bills" },
];

const budgetStatusLabel = (status) =>
  status ? status.charAt(0).toUpperCase() + status.slice(1).replaceAll("_", " ") : "";

const budgetStatusColor = (status) =>
  status === "published" ? "success" : status === "approved" ? "info" : "default";

const money = (value) =>
  value == null
    ? "—"
    : new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(value);

const PropertyFinanceWorkspace = forwardRef(function PropertyFinanceWorkspace(
  { propertyId, showPageHeading = false, onPropertyLoaded, includeSpendTabs = false },
  ref
) {
  const [searchParams, setSearchParams] = useSearchParams();
  const onPropertyLoadedRef = useRef(onPropertyLoaded);
  onPropertyLoadedRef.current = onPropertyLoaded;

  const financeTabs = useMemo(() => {
    if (!includeSpendTabs) return BASE_FINANCE_TABS;
    const invoicesIndex = BASE_FINANCE_TABS.findIndex((tab) => tab.key === "invoices");
    return [
      ...BASE_FINANCE_TABS.slice(0, invoicesIndex + 1),
      ...SPEND_TABS,
      ...BASE_FINANCE_TABS.slice(invoicesIndex + 1),
    ];
  }, [includeSpendTabs]);

  const tabIndex = useMemo(
    () => financeTabs.reduce((acc, tab, index) => ({ ...acc, [tab.key]: index }), {}),
    [financeTabs]
  );

  const requestedTabKey = searchParams.get("tab") || "overview";
  const activeTab = tabIndex[requestedTabKey] ?? 0;
  const activeTabKey = financeTabs[activeTab]?.key || "overview";
  const budgetIdParam = searchParams.get("budgetId");
  const apportionBudgetId = searchParams.get("apportionBudgetId");
  const [editingBudgetId, setEditingBudgetId] = useState(budgetIdParam || "");

  const [property, setProperty] = useState(null);
  const [summary, setSummary] = useState(null);
  const [recalcRefreshKey, setRecalcRefreshKey] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });

  const showToast = useCallback((message, severity = "success") => {
    setToast({ open: true, message, severity });
  }, []);

  const [budgets, setBudgets] = useState([]);
  const [budgetsPage, setBudgetsPage] = useState(1);
  const [budgetsTotalPages, setBudgetsTotalPages] = useState(1);
  const [selectedBudget, setSelectedBudget] = useState(null);
  const [deletingBudgetId, setDeletingBudgetId] = useState(null);

  const [generatingPdfId, setGeneratingPdfId] = useState(null);
  const [regeneratingAllPdfs, setRegeneratingAllPdfs] = useState(false);
  const [generatingInvoices, setGeneratingInvoices] = useState(false);
  const [invoicePreview, setInvoicePreview] = useState(null);
  const [invoicePreviewLoading, setInvoicePreviewLoading] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [notice, setNotice] = useState("");
  const [freshDraftId, setFreshDraftId] = useState(null);
  const [formatOpen, setFormatOpen] = useState(false);
  const [budgetToDelete, setBudgetToDelete] = useState(null);

  useImperativeHandle(ref, () => ({
    openCreateWizard: () => setWizardOpen(true),
  }));

  const goTab = useCallback(
    (key, extra = {}) => {
      const next = new URLSearchParams(searchParams);
      if (key === "overview") next.delete("tab");
      else next.set("tab", key);
      // budgetId belongs to the budget editor. Other tabs, including
      // apportionments, must not inherit the draft that was open there.
      const setsBudget = Object.prototype.hasOwnProperty.call(extra, "budgetId");
      if (key === "budgets") {
        if (!setsBudget && editingBudgetId) next.set("budgetId", String(editingBudgetId));
      } else if (!setsBudget) {
        next.delete("budgetId");
      }
      // po_year belongs to the purchase-order year filter.
      if (key !== "purchase-orders") next.delete("po_year");
      Object.entries(extra).forEach(([k, v]) => {
        if (v == null || v === "") next.delete(k);
        else next.set(k, String(v));
      });
      setSearchParams(next);
    },
    [searchParams, setSearchParams, editingBudgetId]
  );

  const loadCore = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const [propertyData, summaryRes] = await Promise.all([
        getPropertyDetails(propertyId),
        getPropertyFinanceSummary(propertyId),
      ]);
      setProperty(propertyData);
      setSummary(summaryRes.data);
      onPropertyLoadedRef.current?.(propertyData);
    } catch (e) {
      setError(getApiErrorMessage(e, "Failed to load property finance."));
    } finally {
      setLoading(false);
    }
  }, [propertyId]);

  useEffect(() => {
    if (activeTabKey === "budgets") setEditingBudgetId(budgetIdParam || "");
  }, [activeTabKey, budgetIdParam]);

  useEffect(() => {
    setEditingBudgetId(budgetIdParam || "");
    setBudgets([]);
    setSelectedBudget(null);
    setInvoicePreview(null);
    setSummary(null);
    setFreshDraftId(null);
    setNotice("");
    setWizardOpen(false);
    setFormatOpen(false);
    setBudgetToDelete(null);
    // Only the property change should refresh this. budgetIdParam is the id in
    // the URL at that moment, including a draft opened from Work to do.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId]);

  useEffect(() => {
    loadCore();
  }, [loadCore]);

  const loadBudgets = useCallback(async () => {
    const res = await getBudgets(propertyId, budgetsPage);
    setBudgets(res.data.results || []);
    setBudgetsTotalPages(res.data.total_pages || 1);
  }, [propertyId, budgetsPage]);

  const loadBudgetDetail = useCallback(async (budgetId) => {
    try {
      const budgetRes = await getBudget(propertyId, budgetId);
      if (String(budgetRes.data?.property_id) !== String(propertyId)) {
        setSelectedBudget(null);
        return false;
      }
      setSelectedBudget(budgetRes.data);
      return true;
    } catch (e) {
      setSelectedBudget(null);
      return false;
    }
  }, [propertyId]);

  useEffect(() => {
    if (!freshDraftId || String(selectedBudget?.id) !== String(freshDraftId)) return;
    document.getElementById("finance-draft-budget")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [freshDraftId, selectedBudget]);

  useEffect(() => {
    if (activeTabKey === "budgets") {
      loadBudgets();
      if (budgetIdParam) {
        loadBudgetDetail(budgetIdParam).then((found) => {
          if (!found) goTab("budgets", { budgetId: "" });
        });
      } else setSelectedBudget(null);
    }
    if (activeTabKey === "apportionments" && budgetIdParam) {
      loadBudgetDetail(budgetIdParam).then((found) => {
        if (!found) goTab("apportionments", { budgetId: "" });
      });
    }
    if (activeTabKey === "invoices") {
      loadCore();
    }
  }, [
    activeTabKey,
    budgetIdParam,
    loadBudgets,
    loadBudgetDetail,
    goTab,
    loadCore,
    propertyId,
  ]);

  useEffect(() => {
    if (activeTabKey !== "budgets" || budgetIdParam || budgets.length === 0) return;
    goTab("budgets", { budgetId: budgets[0].id });
  }, [activeTabKey, budgetIdParam, budgets, goTab]);

  useEffect(() => {
    if (activeTabKey !== "budgets" || !selectedBudget?.id) return;
    if (Array.isArray(selectedBudget.sections)) return;
    loadBudgetDetail(selectedBudget.id);
  }, [activeTabKey, selectedBudget, loadBudgetDetail]);

  const handleBudgetSave = async (payload) => {
    try {
      const res = await updateBudget(propertyId, selectedBudget.id, payload);
      setSelectedBudget(res.data);
      showToast("Budget saved.");
      loadBudgets();
    } catch (e) {
      if (isConcurrencyConflict(e)) {
        setError("This budget was changed elsewhere. Reloading…");
        loadBudgetDetail(selectedBudget.id);
      } else {
        setError(getApiErrorMessage(e, "Failed to save budget."));
      }
    }
  };

  const handleBudgetStatus = async (action) => {
    try {
      const res = await updateBudgetStatus(propertyId, selectedBudget.id, {
        action,
        row_version: selectedBudget.row_version,
        visible_to_owners: selectedBudget.visible_to_owners,
      });
      setSelectedBudget(res.data);
      showToast(action === "publish" ? "Budget published." : "Budget approved.");
      loadBudgets();
    } catch (e) {
      setError(getApiErrorMessage(e, "Failed to update budget status."));
    }
  };

  const handleOwnerVisibility = async (visibleToOwners) => {
    try {
      const res = await updateBudgetStatus(propertyId, selectedBudget.id, {
        action: "set_owner_visibility",
        row_version: selectedBudget.row_version,
        visible_to_owners: visibleToOwners,
      });
      setSelectedBudget(res.data);
      showToast(visibleToOwners ? "Budget shown to owners." : "Budget hidden from owners.");
      loadBudgets();
    } catch (e) {
      if (isConcurrencyConflict(e)) {
        setError("This budget was changed elsewhere. Reloading…");
        loadBudgetDetail(selectedBudget.id);
      } else {
        setError(getApiErrorMessage(e, "Failed to update owner visibility."));
      }
      throw e;
    }
  };

  const handleDeleteBudget = async (budget) => {
    const nextPage = budgets.length === 1 && budgetsPage > 1 ? budgetsPage - 1 : budgetsPage;

    try {
      setDeletingBudgetId(budget.id);
      setError("");
      await deleteBudget(propertyId, budget.id);

      const budgetsRes = await getBudgets(propertyId, nextPage);
      const nextBudgets = budgetsRes.data.results || [];

      setBudgets(nextBudgets);
      setBudgetsTotalPages(budgetsRes.data.total_pages || 1);
      if (nextPage !== budgetsPage) {
        setBudgetsPage(nextPage);
      }

      if (String(budget.id) === budgetIdParam) {
        const nextSelectedBudget = nextBudgets[0] || null;
        setSelectedBudget(null);
        goTab("budgets", { budgetId: nextSelectedBudget?.id ?? "" });
      }

      showToast("Budget deleted.");
      loadCore();
    } catch (e) {
      setError(getApiErrorMessage(e, "Failed to delete budget."));
    } finally {
      setDeletingBudgetId(null);
    }
  };

  const loadInvoicePreview = useCallback(async () => {
    if (!summary?.invoice_budget_ready) {
      setInvoicePreview(null);
      return;
    }
    try {
      setInvoicePreviewLoading(true);
      const res = await getCurrentInvoicePreview(propertyId);
      setInvoicePreview(res.data);
    } catch (e) {
      setInvoicePreview(null);
      setError(getApiErrorMessage(e, "Failed to load invoice preview."));
    } finally {
      setInvoicePreviewLoading(false);
    }
  }, [propertyId, summary?.invoice_budget_ready]);

  useEffect(() => {
    if (activeTabKey === "invoices" && summary?.invoice_budget_ready) {
      loadInvoicePreview();
    }
  }, [activeTabKey, summary?.invoice_budget_ready, loadInvoicePreview]);

  const handlePreviewInvoices = async () => {
    await loadInvoicePreview();
  };

  const handleGenerateInvoices = async () => {
    try {
      setGeneratingInvoices(true);
      await generateCurrentInvoices(propertyId, {
        invoice_date: new Date().toISOString().slice(0, 10),
      });
      showToast("Draft invoices created.");
      loadInvoicePreview();
    } catch (e) {
      setError(getApiErrorMessage(e, "Failed to generate invoices."));
    } finally {
      setGeneratingInvoices(false);
    }
  };

  const handlePostInvoice = async (invoice) => {
    try {
      await postOwnerInvoice(propertyId, invoice.id, invoice.row_version);
      showToast(`Invoice ${invoice.invoice_number} posted.`);
      loadInvoicePreview();
    } catch (e) {
      setError(getApiErrorMessage(e, "Failed to post invoice."));
    }
  };

  const handleGenerateInvoicePdf = async (invoice) => {
    try {
      setGeneratingPdfId(invoice.id);
      setError("");
      const res = await generateOwnerInvoicePdf(propertyId, invoice.id);
      const updated = res.data;
      showToast(`PDF generated for ${invoice.invoice_number}.`);
      if (updated.pdf_download_url) {
        window.open(updated.pdf_download_url, "_blank", "noopener,noreferrer");
      }
      loadInvoicePreview();
    } catch (e) {
      setError(getApiErrorMessage(e, "Failed to generate invoice PDF."));
    } finally {
      setGeneratingPdfId(null);
    }
  };

  const handleRegenerateAllInvoicePdfs = async () => {
    const invoices = (invoicePreview?.units || []).filter(
      (unit) => unit.invoice_id && unit.invoice_status === "draft"
    );
    if (!invoices.length) return;

    try {
      setRegeneratingAllPdfs(true);
      setError("");
      for (const invoice of invoices) {
        await generateOwnerInvoicePdf(propertyId, invoice.invoice_id);
      }
      showToast(`Regenerated ${invoices.length} invoice PDF${invoices.length === 1 ? "" : "s"}.`);
      loadInvoicePreview();
    } catch (e) {
      setError(getApiErrorMessage(e, "Failed to regenerate invoice PDFs."));
    } finally {
      setRegeneratingAllPdfs(false);
    }
  };

  const handlePostAll = async () => {
    try {
      const res = await postAllCurrentDraftInvoices(propertyId);
      showToast(`Posted ${res.data.posted_count} invoices.`);
      loadInvoicePreview();
    } catch (e) {
      setError(getApiErrorMessage(e, "Failed to post invoices."));
    }
  };

  const invoiceBudgetReady = Boolean(summary?.invoice_budget_ready);

  return (
    <Box>
      {showPageHeading && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="overline" color="text.secondary">
            Finance
          </Typography>
          <Typography variant="h4" fontWeight={700}>
            {property?.name || "Property finance"}
          </Typography>
        </Box>
      )}

      {notice && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setNotice("")}>
          {notice}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      <Tabs
        value={activeTab}
        onChange={(_, idx) => goTab(financeTabs[idx].key)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 3 }}
      >
        {financeTabs.map((t) => (
          <Tab key={t.key} label={t.label} />
        ))}
      </Tabs>

      {activeTabKey === "overview" && (
        <>
          {loading ? (
            <Skeleton height={200} />
          ) : (
            <>
              {summary?.service_charge_recalculation && (
                <Alert severity="warning" sx={{ mb: 2, whiteSpace: "pre-line" }}>
                  {summary.service_charge_recalculation.reason ||
                    "Budget or apportionment changes require a service charge recalculation."}
                </Alert>
              )}
              {Boolean(
                summary?.current_fiscal_year_label ||
                  Number(summary?.budget_total) ||
                  Number(summary?.collected_ytd) ||
                  Number(summary?.spent_ytd) ||
                  Number(summary?.outstanding_ytd)
              ) && (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
                    gap: 2,
                    mb: 3,
                  }}
                >
                  <KpiTile label="Budget total" value={money(summary?.budget_total)} />
                  <KpiTile label="Collected YTD" value={money(summary?.collected_ytd)} />
                  <KpiTile label="Spent YTD" value={money(summary?.spent_ytd)} />
                  <KpiTile label="Outstanding" value={money(summary?.outstanding_ytd)} />
                </Box>
              )}
              <ServiceChargeMechanismPanel
                propertyId={propertyId}
                onMessage={showToast}
                onError={setError}
                onChanged={() => {
                  loadCore();
                  setRecalcRefreshKey((k) => k + 1);
                }}
              />
              <ServiceChargeRecalculationPanel
                propertyId={propertyId}
                refreshKey={recalcRefreshKey}
                onApplied={loadCore}
                onMessage={showToast}
                onError={setError}
              />
              <Panel title="Arrears" noBodyPadding={false}>
                {(summary?.arrears?.results || []).length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No arrears over three months.
                  </Typography>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Unit</TableCell>
                          <TableCell>Owner</TableCell>
                          <TableCell align="right">Outstanding</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(summary?.arrears?.results || []).map((row) => (
                          <TableRow key={row.unit_id}>
                            <TableCell>
                              <RouterLink to={`/units/${row.unit_id}/statement`}>
                                {row.unit_name}
                              </RouterLink>
                            </TableCell>
                            <TableCell>{row.owner_name || "—"}</TableCell>
                            <TableCell align="right">{money(row.outstanding)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Panel>
            </>
          )}
        </>
      )}

      {activeTabKey === "budgets" && (
        <>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => setFormatOpen((open) => !open)}
            >
              {formatOpen ? "Hide charge list" : "Edit charge list"}
            </Button>
          </Box>
          {formatOpen && (
            <BudgetFormatEditor
              selectedBudget={selectedBudget}
              budgetId={budgetIdParam}
              onSaved={async () => {
                if (budgetIdParam) await loadBudgetDetail(budgetIdParam);
                loadBudgets();
              }}
              propertyId={propertyId}
              propertyName={property?.name}
              onMessage={(msg) => {
                showToast(msg);
              }}
              onError={setError}
            />
          )}
          <Box sx={{ display: "grid", gridTemplateColumns: { md: "320px 1fr" }, gap: 2, alignItems: "start" }}>
            <Panel
              title="Budgets"
              noBodyPadding={false}
              right={
                <Button size="small" startIcon={<AddIcon />} onClick={() => setWizardOpen(true)}>
                  Create budget
                </Button>
              }
            >
              {budgets.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No budgets yet. Create one to choose the year and the charges.
                </Typography>
              ) : (
                budgets.map((b) => {
                  const selected = String(b.id) === String(budgetIdParam);
                  return (
                    <Box
                      key={b.id}
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1,
                        p: 1.5,
                        mb: 1,
                        borderRadius: 1,
                        border: 1,
                        borderColor: selected ? "text.primary" : "divider",
                        bgcolor: selected ? "action.selected" : "transparent",
                      }}
                    >
                      <Box
                        component="button"
                        type="button"
                        onClick={() => goTab("budgets", { budgetId: b.id })}
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          p: 0,
                          border: 0,
                          bgcolor: "transparent",
                          textAlign: "left",
                          cursor: "pointer",
                          font: "inherit",
                          color: "inherit",
                        }}
                      >
                        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                          <Typography variant="body2" fontWeight={600}>
                            {b.name}
                          </Typography>
                          <Typography variant="body2" sx={{ flexShrink: 0 }}>
                            {money(b.total)}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                          {fiscalYearLabel(b.fiscal_year) || "No financial year"}
                        </Typography>
                        <Chip
                          size="small"
                          label={budgetStatusLabel(b.status)}
                          color={budgetStatusColor(b.status)}
                          sx={{ mt: 0.75 }}
                        />
                      </Box>
                      {b.status === "draft" && (
                        <Button
                          variant="outlined"
                          size="small"
                          color="error"
                          aria-label={`Delete ${b.name}`}
                          disabled={deletingBudgetId === b.id}
                          onClick={() => setBudgetToDelete(b)}
                          sx={{ minWidth: 0, px: 1 }}
                        >
                          <DeleteIcon fontSize="small" />
                        </Button>
                      )}
                    </Box>
                  );
                })
              )}
              {budgetsTotalPages > 1 && (
                <Pagination
                  size="small"
                  count={budgetsTotalPages}
                  page={budgetsPage}
                  onChange={(_, p) => setBudgetsPage(p)}
                  sx={{ mt: 1 }}
                />
              )}
            </Panel>
            <Box id="finance-draft-budget">
              {selectedBudget ? (
                <BudgetEditor
                  budget={selectedBudget}
                  onSave={handleBudgetSave}
                  onStatus={handleBudgetStatus}
                  onOwnerVisibility={handleOwnerVisibility}
                />
              ) : (
                <Panel title="Budget" noBodyPadding={false}>
                  <Typography variant="body2" color="text.secondary">
                    {budgets.length === 0
                      ? "A new budget opens here after you create it."
                      : "Select a budget to enter its amounts."}
                  </Typography>
                </Panel>
              )}
            </Box>
          </Box>
          <Dialog open={Boolean(budgetToDelete)} onClose={() => setBudgetToDelete(null)}>
            <DialogTitle>Delete this draft?</DialogTitle>
            <DialogContent>
              <DialogContentText>
                {`Delete "${budgetToDelete?.name || "this budget"}"? This cannot be undone.`}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button startIcon={<CloseIcon />} onClick={() => setBudgetToDelete(null)}>
                Cancel
              </Button>
              <Button
                color="error"
                variant="outlined"
                startIcon={<DeleteIcon />}
                onClick={() => {
                  const budget = budgetToDelete;
                  setBudgetToDelete(null);
                  if (budget) handleDeleteBudget(budget);
                }}
              >
                Delete
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}

      {activeTabKey === "fiscal-years" && (
        <FiscalYearsPanel
          propertyId={propertyId}
          financialYearStartDay={property?.financial_year_start_day}
          financialYearStartMonth={property?.financial_year_start_month}
          onMessage={showToast}
          onError={setError}
        />
      )}

      {activeTabKey === "apportionments" && (
        <ApportionmentsPanel
          propertyId={propertyId}
          propertyUnits={property?.units || []}
          budgetIdParam={apportionBudgetId || summary?.current_budget_id || ""}
          onBudgetChange={(id) => goTab("apportionments", { apportionBudgetId: id })}
          onMessage={showToast}
          onError={setError}
        />
      )}

      {activeTabKey === "invoices" && (
        <Panel title="Owner invoices" noBodyPadding={false}>
          <Box sx={{ mb: 2 }}>
            {summary?.current_fiscal_year_label ? (
              <Typography variant="body2" color="text.secondary">
                Current fiscal year: {summary.current_fiscal_year_label}
                {summary.current_budget_name
                  ? ` · Budget: ${summary.current_budget_name}${
                      summary.current_budget_status
                        ? ` (${summary.current_budget_status})`
                        : ""
                    }`
                  : ""}
              </Typography>
            ) : (
              <Alert severity="warning" sx={{ mb: 0 }}>
                Set a current fiscal year before generating invoices.
              </Alert>
            )}
            {summary?.current_fiscal_year_label && !invoiceBudgetReady && (
              <Alert severity="warning" sx={{ mt: 1, mb: 0 }}>
                {summary.current_budget_name
                  ? `The budget "${summary.current_budget_name}" must be approved before generating invoices.`
                  : "Create and approve a budget for the current fiscal year before generating invoices."}
              </Alert>
            )}
            {summary?.current_fiscal_year_label && !property?.service_charge_mechanism && (
              <Alert severity="warning" sx={{ mt: 1, mb: 0 }}>
                Choose a service charge mechanism on the Overview tab. Invoices need each unit's share of the estate charge, and that share is not set yet.
              </Alert>
            )}
          </Box>
          {property?.service_charge_mechanism && (
          <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
            <Button
              variant="contained"
              onClick={handleGenerateInvoices}
              disabled={
                !invoiceBudgetReady || invoicePreview?.new_unit_count === 0 || generatingInvoices
              }
              startIcon={
                generatingInvoices ? <CircularProgress size={18} color="inherit" /> : null
              }
            >
              Generate drafts
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={handlePostAll}
              disabled={!invoiceBudgetReady}
            >
              Post all drafts
            </Button>
          </Box>
          )}
          {property?.service_charge_mechanism && (
          <OwnerInvoicePreviewPanel
            preview={invoicePreview}
            loading={invoicePreviewLoading}
            onRefresh={invoiceBudgetReady ? handlePreviewInvoices : null}
            onPostInvoice={handlePostInvoice}
            onGeneratePdf={handleGenerateInvoicePdf}
            generatingPdfId={generatingPdfId}
            onRegenerateAll={handleRegenerateAllInvoicePdfs}
            regeneratingAll={regeneratingAllPdfs}
          />
          )}
        </Panel>
      )}

      {includeSpendTabs && activeTabKey === "purchase-orders" && (
        <PurchaseOrdersPanel propertyId={propertyId} onMessage={showToast} />
      )}

      {includeSpendTabs && activeTabKey === "supplier-bills" && (
        <SupplierBillsPanel propertyId={propertyId} onMessage={showToast} />
      )}

      {activeTabKey === "bank" && (
        <BankAccountsPanel
          propertyId={propertyId}
          property={property}
          onMessage={showToast}
          onError={setError}
        />
      )}

      {activeTabKey === "reconciliation" && (
        <BankReconciliationPanel
          propertyId={propertyId}
          onMessage={showToast}
          onError={setError}
        />
      )}

      {activeTabKey === "year-end" && (
        <YearEndPackPanel key={propertyId} propertyId={propertyId} onError={setError} />
      )}

      <CreateBudgetWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        propertyId={propertyId}
        propertyName={property?.name}
        financialYearStartDay={property?.financial_year_start_day}
        financialYearStartMonth={property?.financial_year_start_month}
        onSavedProgress={setNotice}
        onCreated={(budget) => {
          setFreshDraftId(budget.id);
          goTab("budgets", { budgetId: budget.id });
          showToast(
            "This budget is a draft. Enter the amounts, then send it for approval when it is ready."
          );
          loadBudgets();
        }}
      />

      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          onClose={() => setToast((t) => ({ ...t, open: false }))}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
});

export default PropertyFinanceWorkspace;
